import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageCircle, Star, ArrowUpRight, Award, Sparkles, Droplets, Heart, ShieldCheck, Check, Phone, MapPin } from "lucide-react";
import { SITE, SERVICES, WHY_US, EXPERIENCE, TESTIMONIALS, FAQS, GALLERY, CAMPAIGNS, STATS } from "@/data/site";
import { Reveal, SectionEyebrow, SectionTitle } from "@/components/Reveal";
import AppointmentDialog from "@/components/AppointmentDialog";
import { Marquee } from "@/components/Marquee";
import { CountUp } from "@/components/CountUp";
import SEO from "@/components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ICONS = { Award, Sparkles, Droplets, Heart, ShieldCheck, Star };

const HERO_IMAGE = "https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&w=2000&q=85";
const ABOUT_IMG_1 = "https://images.unsplash.com/photo-1611441085337-56f4d0ea0ae2?auto=format&fit=crop&w=1200&q=85";
const ABOUT_IMG_2 = "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?auto=format&fit=crop&w=900&q=85";

export default function Home() {
  const [apptOpen, setApptOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 180]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0]);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Bursa Masaj Salonu — Diamond Life",
    url: SITE.baseUrl,
    inLanguage: "tr-TR",
    publisher: {
      "@type": "MassageBusiness",
      name: "Diamond Life Bursa Masaj Salonu",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="Premium Spa & Masaj"
        description="Bursa Masaj Salonu Diamond Life — Nilüfer'de sertifikalı terapistler, organik yağlar ve butik atmosferde klasik, aromaterapi, medikal, Bali, çift ve sıcak taş masajı."
        path="/"
        schema={homeSchema}
        breadcrumb={[{ name: "Anasayfa", path: "/" }]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 pv-zoom bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
        </motion.div>
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-forest-deep/65 via-forest-deep/35 to-cream" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_20%_30%,rgba(183,139,72,0.16),transparent_55%)]" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-20 min-h-[100svh] flex flex-col items-center justify-center text-center px-6 pt-32 pb-24 md:pt-28 md:pb-28">
          <Reveal delay={0.15}>
            <a
              data-testid="hero-location-badge"
              href={SITE.mapsShare}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-cream/30 backdrop-blur-md bg-cream/[0.06] hover:bg-cream/15 hover:border-bronze-soft/60 transition-all duration-500"
            >
              <MapPin className="w-3.5 h-3.5 text-bronze-soft shrink-0" />
              <span className="text-[10px] tracking-[0.18em] sm:tracking-[0.32em] uppercase text-cream font-medium whitespace-nowrap">Bursa Masaj Salonu</span>
              <span className="w-px h-3 bg-cream/30" />
              <span className="text-[10px] tracking-[0.18em] sm:tracking-[0.32em] uppercase text-cream/75 font-light whitespace-nowrap">Premium Spa</span>
            </a>
          </Reveal>

          <motion.h1
            data-testid="hero-title"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="font-display text-[2.8rem] sm:text-[4.2rem] lg:text-[6rem] leading-[1.02] text-cream font-thin max-w-5xl mt-7 tracking-[-0.04em]"
            style={{ fontWeight: 200 }}
          >
            Diamond Life <span className="pv-italic bronze-text">Masaj</span>
            <span className="block text-[0.32em] tracking-[0.05em] text-cream/85 mt-4 font-light uppercase">Bursa Masaj Salonu · Premium Spa</span>
          </motion.h1>

          <Reveal delay={0.8}>
            <p className="mt-7 text-cream/80 font-light text-base sm:text-[1.05rem] max-w-2xl leading-[1.75]">
              Sertifikalı terapistler, organik yağlar ve butik atmosferde klasik, aromaterapi, medikal, Bali, çift ve sıcak taş masajı. Hijyenik, premium ve gerçekten dinlendirici.
            </p>
          </Reveal>

          <Reveal delay={1.0}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              <button
                data-testid="hero-appointment-btn"
                onClick={() => setApptOpen(true)}
                className="pv-btn pv-btn-bronze"
              >
                Online Randevu Al
              </button>
              <a
                data-testid="hero-whatsapp-btn"
                href={`https://wa.me/${SITE.whatsappRaw}`}
                target="_blank"
                rel="noreferrer"
                className="pv-btn pv-btn-light"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                data-testid="hero-phone-btn"
                href={`tel:${SITE.phoneRaw}`}
                className="pv-btn pv-btn-light"
              >
                <Phone className="w-4 h-4" /> Hemen Ara
              </a>
            </div>
          </Reveal>

          {/* Trust indicators */}
          <Reveal delay={1.15}>
            <div className="mt-12 md:mt-14 grid grid-cols-2 gap-x-10 gap-y-6 sm:flex sm:flex-wrap items-center justify-center sm:gap-8 md:gap-14">
              {[
                { v: "4.9/5", l: "Puan" },
                { v: "186+", l: "Google Yorumu" },
                { v: "Sertifikalı", l: "Uzman Kadro" },
                { v: "Premium", l: "Hijyenik Ortam" },
              ].map((t) => (
                <div key={t.l} className="text-center">
                  <div className="font-display text-2xl text-cream">{t.v}</div>
                  <div className="text-[10px] tracking-luxury uppercase text-cream/85 mt-1">{t.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </motion.div>

        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 text-[10px] tracking-luxury uppercase text-cream/55">
          <span>Aşağı Kaydır</span>
          <ChevronDown className="w-4 h-4 pv-down" />
        </div>
      </section>

      {/* STATS BAND */}
      <section data-testid="stats-section" className="relative mt-8 md:-mt-16 z-30 px-4 md:px-10">
        <div className="mx-auto max-w-[1300px] bg-bone border border-pv shadow-[0_30px_80px_-30px_rgba(31,64,54,0.18)]">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.04}>
                <div className="px-4 py-6 text-center border-pv-soft border-l first:border-l-0 sm:[&:nth-child(4n+1)]:border-l-0 lg:[&:nth-child(4n+1)]:border-l">
                  <div className="font-display text-2xl md:text-[1.85rem] text-forest-deep tracking-tight whitespace-nowrap" style={{ fontWeight: 300 }}><CountUp value={s.value} /></div>
                  <div className="text-[9px] tracking-luxury uppercase text-muted-pv mt-2 font-medium">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KAYAN ŞERİT — hizmetler & sloganlar */}
      <div className="mt-16 md:mt-20">
        <Marquee />
      </div>

      {/* INTRO */}
      <section className="relative pt-24 md:pt-32 pb-24 md:pb-32 px-6 md:px-12 lg:px-24 leaf-pattern">
        <div className="mx-auto max-w-[1300px] grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <SectionEyebrow>Hoş Geldiniz</SectionEyebrow>
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] text-forest-deep font-normal leading-[1.05] mt-6 tracking-tight">
                Bursa'da <span className="pv-italic bronze-text">sessizlik</span> ile başlayan butik bir kaçış.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-ink-soft/75 font-light text-base sm:text-lg leading-[1.8] max-w-xl">
                Diamond Life Masaj Bursa; sertifikalı ve deneyimli terapist kadrosuyla, butik bir atmosferde premium masaj ve spa deneyimi sunan bir merkez. Klasik İsveç masajı, aromaterapi, medikal, geleneksel Bali masajı ve çift masajı hizmetlerini Avrupa menşeili organik yağlarla buluşturuyoruz.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/hakkimizda" data-testid="intro-about-link" className="pv-btn">
                  Hikâyemiz <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/hizmetler" className="pv-btn pv-btn-solid">
                  Tüm Hizmetler
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-12 gap-4">
            <Reveal className="col-span-7" delay={0.1}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={ABOUT_IMG_1} alt="Diamond Life Spa" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.6s]" loading="lazy" />
                <div className="absolute bottom-4 left-4 pv-tag bg-bone">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" /> Butik Atmosfer
                </div>
              </div>
            </Reveal>
            <div className="col-span-5 flex flex-col gap-4">
              <Reveal delay={0.25}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={ABOUT_IMG_2} alt="Bali Masaj Odası" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.6s]" loading="lazy" />
                </div>
              </Reveal>
              <Reveal delay={0.35}>
                <div className="relative aspect-square border border-bronze/30 bg-cream-warm flex flex-col justify-center items-center text-center p-3 sm:p-5">
                  <div className="font-display text-3xl sm:text-5xl bronze-text leading-none"><CountUp value="12+" /></div>
                  <div className="text-[9px] sm:text-[10px] tracking-[0.16em] sm:tracking-luxury uppercase text-muted-pv mt-2 leading-relaxed">Yıllık Tecrübe</div>
                  <div className="absolute top-3 left-3 w-2 h-2 border-l border-t border-bronze" />
                  <div className="absolute bottom-3 right-3 w-2 h-2 border-r border-b border-bronze" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HİZMETLER */}
      <section id="services" data-testid="services-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-cream-warm">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <Reveal>
              <SectionTitle eyebrow="Hizmetlerimiz" title='Bursa Masaj Salonu <span class="pv-italic bronze-text">Hizmetleri</span>' />
              <p className="mt-5 text-ink-soft/70 font-light text-base sm:text-lg max-w-xl leading-relaxed">
                Sertifikalı terapistlerle 6 ana masaj türünde butik premium hizmet.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/hizmetler" data-testid="services-view-all" className="pv-btn">
                Tüm Hizmetler <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link
                  to={`/hizmetler/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block relative overflow-hidden bg-bone border border-pv pv-lift"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1.6s] group-hover:scale-110"
                    />
                    <div className="absolute top-3 left-3 pv-tag bg-bone/95">Premium</div>
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-forest text-cream text-[10px] tracking-luxury uppercase">
                      {s.duration}
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-3xl text-forest-deep font-normal group-hover:text-bronze-deep transition-colors leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm text-ink-soft/70 font-light leading-relaxed line-clamp-2">{s.short}</p>
                    <div className="mt-6 pt-5 border-t border-pv-soft flex items-center justify-between">
                      <span className="text-[10px] tracking-luxury uppercase text-bronze-deep font-medium">İncele</span>
                      <ArrowUpRight className="w-4 h-4 text-bronze transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAMPAIGNS */}
      <section data-testid="campaigns-section" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1300px]">
          <Reveal>
            <SectionTitle
              eyebrow="Özel Kampanyalarımız"
              title='Özel <span class="pv-italic bronze-text">Fiyatlar</span> ve Paketler'
              subtitle="Uzman ekip · hijyenik ortam · kaliteli hizmet. Tüm masajlarımızda kese köpük bizden hediye."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CAMPAIGNS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.1}>
                <div className="relative border border-bronze/30 bg-cream-warm p-8 md:p-10 pv-lift overflow-hidden h-full">
                  <div className="absolute top-0 right-0 px-3 py-1 bg-forest text-cream text-[10px] tracking-luxury uppercase">
                    {c.duration}
                  </div>
                  <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-bronze/10" />
                  <div className="relative">
                    <div className="text-[10px] tracking-luxury uppercase text-muted-pv mb-3">{c.badge} · Özel Fiyat</div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-6xl md:text-7xl bronze-text leading-none">{c.price}</span>
                      <span className="text-sm tracking-luxury uppercase text-bronze-deep font-medium">TL</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.7rem] text-forest-deep font-normal mt-6 leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sm text-ink-soft/70 font-light leading-relaxed">{c.note}</p>
                    <button
                      onClick={() => setApptOpen(true)}
                      data-testid={`campaign-cta-${i}`}
                      className="mt-7 inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-bronze-deep font-medium pv-underline-link"
                    >
                      {c.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-cream-warm border-y border-pv">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionTitle
              eyebrow="Neden Biz"
              title='Diamond Life <span class="pv-italic bronze-text">Farkı</span>'
              subtitle="Bursa'da premium masaj salonu deneyiminin standartlarını yeniden tanımlayan 6 temel değer."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_US.map((w, i) => {
              const Icon = ICONS[w.icon] || Award;
              return (
                <Reveal key={w.title} delay={i * 0.08}>
                  <div className="group relative p-9 border border-pv bg-bone pv-lift h-full">
                    <div className="w-14 h-14 rounded-full bg-forest/8 flex items-center justify-center mb-7 group-hover:bg-forest group-hover:text-cream transition-colors">
                      <Icon className="w-5 h-5 text-forest group-hover:text-cream" />
                    </div>
                    <h3 className="font-display text-2xl text-forest-deep font-normal">{w.title}</h3>
                    <p className="mt-3 text-sm text-ink-soft/70 font-light leading-relaxed">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPERIENCE TIMELINE */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1200px]">
          <Reveal>
            <SectionTitle
              eyebrow="Süreç"
              title='Seans Akışı <span class="pv-italic bronze-text">4 adımda</span>'
              subtitle="Standart şablonlar değil, kişiye özel premium süreç."
            />
          </Reveal>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXPERIENCE.map((e, i) => (
              <Reveal key={e.step} delay={i * 0.1}>
                <div className="relative border border-pv bg-bone p-7 h-full pv-lift">
                  <div className="font-display text-6xl bronze-text leading-none mb-5">{e.step}</div>
                  <h3 className="font-display text-2xl text-forest-deep font-normal leading-tight">{e.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft/70 font-light leading-relaxed">{e.desc}</p>
                  <span className="absolute top-7 right-7 w-2 h-2 rounded-full bg-bronze" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT BAND */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-forest text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-15 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1601479604588-68d9e6d386b5?auto=format&fit=crop&w=1600&q=80')" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-deep via-forest/85 to-forest" />

        <div className="relative mx-auto max-w-[1200px] grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-luxury uppercase text-bronze-soft font-medium">Hakkımızda</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cream font-normal mt-5 leading-[1.05] tracking-tight">
              Bursa Premium <span className="pv-italic text-bronze-soft">Masaj Adresi</span>
            </h2>
            <p className="mt-6 text-cream/75 font-light text-base sm:text-lg leading-[1.8] max-w-2xl">
              Diamond Life Masaj Bursa, butik bir masaj ve spa merkezidir. Sertifikalı terapist kadromuzla; klasik İsveç masajı, aromaterapi, medikal, geleneksel Bali masajı, sıcak taş ve çift masajı hizmetlerini premium kalitede sunuyoruz.
            </p>
            <p className="mt-4 text-cream/65 font-light text-base leading-[1.8] max-w-2xl">
              Her seans öncesi misafirlerimizin ihtiyaçları dinlenir, kişiselleştirilmiş bir terapi planı oluşturulur. Avrupa menşeili organik esansiyel yağlar, tek kullanımlık örtüler ve uluslararası hijyen protokolleri standartlarımızın temel taşıdır.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {["Hijyen Sertifikalı İşletme", "Akredite Terapistler", "Organik Yağlar", "Özel Suit Odalar"].map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-cream/85">
                  <Check className="w-4 h-4 text-bronze-soft" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/hakkimizda" className="pv-btn pv-btn-light">Daha Fazla <ArrowUpRight className="w-4 h-4" /></Link>
              <button onClick={() => setApptOpen(true)} className="pv-btn pv-btn-bronze">Randevu Al</button>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1652720919938-81e2bf084a36?auto=format&fit=crop&w=1100&q=85" alt="Diamond Life Atmosphere" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute -bottom-6 -left-6 bg-bone p-5 border border-pv">
                <div className="flex items-center gap-2 text-bronze">
                  {[...Array(5)].map((_, k) => <Star key={k} className="w-3 h-3 fill-current" />)}
                </div>
                <div className="font-display text-3xl text-forest-deep mt-2">4.9/5</div>
                <div className="text-[10px] tracking-luxury uppercase text-muted-pv">186+ Google Yorumu</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <Reveal>
              <SectionTitle eyebrow="Galeri · Mekanımız" title='Premium <span class="pv-italic bronze-text">Atmosferi</span> Keşfet' subtitle="Hijyenik, ferah ve sessiz butik salonumuzdan profesyonel kareler." />
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/galeri" data-testid="gallery-view-all" className="pv-btn">
                Tüm Galeri <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GALLERY.slice(0, 8).map((src, i) => (
              <Reveal key={src + i} delay={i * 0.05}>
                <div className={`relative overflow-hidden ${i % 5 === 0 ? "aspect-[4/5]" : "aspect-square"}`}>
                  <img src={src} alt={`Bursa Masaj Salonu Diamond Life - ${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1.4s]" />
                  <div className="absolute inset-0 bg-forest-deep/0 hover:bg-forest-deep/20 transition-colors duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-cream-warm border-y border-pv">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionTitle
              eyebrow="Müşteri Yorumları"
              title='Misafirlerimiz <span class="pv-italic bronze-text">Ne Diyor?</span>'
              subtitle="4.9/5 puan · 186+ gerçek Google yorumu."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <Reveal key={t.name} delay={i * 0.07}>
                <div className="p-8 border border-pv bg-bone h-full pv-lift">
                  <div className="flex items-center gap-1 text-bronze">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-5 text-ink-soft/85 font-light leading-relaxed text-[15px]">"{t.text}"</p>
                  <div className="mt-7 pt-5 border-t border-pv-soft flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center font-display text-lg">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-display text-lg text-forest-deep">{t.name}</div>
                        <div className="text-[10px] tracking-luxury uppercase text-muted-pv mt-0.5">{t.service}</div>
                      </div>
                    </div>
                    <div className="text-[9px] tracking-luxury uppercase text-bronze-deep">{t.when}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1100px] grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionTitle eyebrow="SSS" title='Sık <span class="pv-italic bronze-text">Sorulanlar</span>' />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-ink-soft/70 font-light text-sm leading-relaxed">
                Aklınızı kurcalayan başka bir konu varsa, terapist ekibimiz WhatsApp üzerinden 30 dakika içinde dönüş yapar.
              </p>
              <a href={`https://wa.me/${SITE.whatsappRaw}`} target="_blank" rel="noreferrer" className="pv-btn mt-7">
                <MessageCircle className="w-4 h-4" /> WhatsApp ile Yaz
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border border-pv bg-bone px-6"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="hover:no-underline text-left py-5 text-forest-deep font-medium text-base">
                    <span className="flex items-start gap-4">
                      <span className="text-bronze text-xs tracking-luxury">0{i + 1}</span>
                      <span>{f.q}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-soft/75 font-light text-sm leading-[1.8] pb-5 pl-10">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* SEO CONTENT BAND */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-cream-warm border-t border-pv">
        <div className="mx-auto max-w-[1100px]">
          <Reveal>
            <p className="text-[11px] tracking-luxury uppercase text-bronze-deep font-medium">Bursa Masaj Salonu</p>
            <h2 className="font-display text-3xl md:text-4xl text-forest-deep mt-4 leading-[1.15] tracking-[-0.02em]" style={{ fontWeight: 250 }}>
              Bursa Nilüfer'in <span className="pv-italic bronze-text">premium</span> masaj salonu adresi
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 text-ink-soft/85 font-light text-[15px] leading-[1.85]">
              <p>
                <strong className="text-forest-deep font-medium">Diamond Life Bursa Masaj Salonu</strong>, Nilüfer Fethiye Mahallesi'nde konumlanan butik bir masaj ve spa merkezidir. Bursa merkez, Görükle, Mudanya ve Çekirge bölgelerinden kolaylıkla ulaşabileceğiniz konumumuzda, sertifikalı terapist kadromuzla premium kalitede 6 ana masaj hizmeti sunuyoruz.
              </p>
              <p>
                Bursa masaj salonu arayışında olan misafirlerimize <Link to="/hizmetler/klasik-masaj" className="text-bronze-deep underline-offset-4 hover:underline">klasik İsveç masajı</Link>, <Link to="/hizmetler/aromaterapi-masaji" className="text-bronze-deep underline-offset-4 hover:underline">aromaterapi masajı</Link>, <Link to="/hizmetler/medikal-masaj" className="text-bronze-deep underline-offset-4 hover:underline">medikal masaj</Link>, <Link to="/hizmetler/bali-masaji" className="text-bronze-deep underline-offset-4 hover:underline">geleneksel Bali masajı</Link>, <Link to="/hizmetler/cift-masaji" className="text-bronze-deep underline-offset-4 hover:underline">çift masajı</Link> ve <Link to="/hizmetler/sicak-tas-masaji" className="text-bronze-deep underline-offset-4 hover:underline">sıcak taş masajı</Link> hizmetlerini Avrupa menşeili organik yağlarla buluşturuyoruz.
              </p>
              <p>
                Her seans öncesi konsültasyon ile misafirimizin ihtiyaçları dinlenir, kişiselleştirilmiş bir terapi planı oluşturulur. Tek kullanımlık örtüler, hijyenik havlu setleri ve uluslararası hijyen protokolleri Bursa Masaj Salonu standartlarımızın temel taşıdır.
              </p>
              <p>
                Bursa'nın gerçek butik spa merkezini deneyimlemek için <Link to="/iletisim" className="text-bronze-deep underline-offset-4 hover:underline">online randevu</Link> alabilir veya {SITE.phoneDisplay} numaralı hattımızdan bize ulaşabilirsiniz. Aynı gün randevu mümkündür; WhatsApp ile 30 dakika içinde dönüş yapıyoruz.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-9 flex flex-wrap gap-2">
              {[
                "Bursa Masaj Salonu",
                "Nilüfer Masaj Salonu",
                "Bursa Spa",
                "Görükle Masaj",
                "Aromaterapi Bursa",
                "Medikal Masaj Bursa",
                "Sıcak Taş Masajı Bursa",
                "Çift Masajı Bursa",
                "Bali Masajı Bursa",
                "Klasik Masaj Bursa",
              ].map((t) => (
                <span key={t} className="text-[10px] tracking-luxury uppercase text-forest border border-pv bg-bone px-3 py-1.5 rounded-sm font-medium">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden bg-forest-deep">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&w=1900&q=80')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-deep via-forest-deep/85 to-forest-deep" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(183,139,72,0.22),transparent_60%)]" />

        <div className="relative mx-auto max-w-3xl text-center">
          <Reveal>
            <p className="text-[11px] tracking-luxury uppercase text-bronze-soft font-medium">Rezervasyon</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.8rem] text-cream font-normal leading-[1.05] mt-6 tracking-tight">
              Hemen <span className="pv-italic text-bronze-soft">Randevu</span> Alın
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-cream/70 font-light text-base sm:text-lg max-w-xl mx-auto">
              Premium spa deneyiminize bir telefon kadar yakınsınız. Aynı gün randevu mümkündür.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button data-testid="cta-appt-btn" onClick={() => setApptOpen(true)} className="pv-btn pv-btn-bronze">
                Online Randevu
              </button>
              <a data-testid="cta-whatsapp-btn" href={`https://wa.me/${SITE.whatsappRaw}`} target="_blank" rel="noreferrer" className="pv-btn pv-btn-light">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a data-testid="cta-phone-btn" href={`tel:${SITE.phoneRaw}`} className="pv-btn pv-btn-light">
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <AppointmentDialog open={apptOpen} onOpenChange={setApptOpen} />
    </>
  );
}
